import { CrosshairMode, createChart } from 'lightweight-charts';
import { useChartContext } from '../components/chart.context';
import { IndicatorType } from '@server/data-feed/schemas/output.schema';
import { useCallback } from 'react';
import { useChartStreamContext } from '../components/chart-stream.context';

export const useSubplotSeries = () => {
  const {
    indicatorContainerRefs,
    indicatorChartRefs,
    indicatorSeriesRefs,
    emitSubplotChartCreated
  } = useChartContext();
  const { formState } = useChartStreamContext();
  const isLiveTrading = formState.marketConfig?.tradingMode === 'LIVE';

  const initializeChart = (type: IndicatorType, isLastIndicator: boolean) => {
    const ref = indicatorContainerRefs.current?.[type];
    if (!ref) return null;

    const chart = createChart(ref, {
      width: ref.clientWidth,
      height: ref.clientHeight,
      layout: { background: { color: 'transparent' }, textColor: '#DDD' },
      grid: {
        vertLines: { color: '#444', visible: false },
        horzLines: { color: '#444', visible: false }
      },
      // Indicator panes should not display price highlighting/labels.
      crosshair: {
        mode: CrosshairMode.Normal,
        horzLine: { visible: false, labelVisible: false }
      },
      timeScale: {
        visible: !isLiveTrading && isLastIndicator
      }
    });

    if (!indicatorChartRefs.current[type]) {
      indicatorChartRefs.current[type] = [];
    }
    indicatorChartRefs.current[type]?.push(chart);
    emitSubplotChartCreated();
    return chart;
  };

  const initializeChartAndSeriesArray = useCallback(
    (type: IndicatorType, isLastIndicator: boolean) => {
      // Initialize the chart
      const chart = initializeChart(type, isLastIndicator);
      if (!chart) {
        throw new Error(`Chart initialization failed for type: ${type}`);
      }

      // Ensure the series array exists
      if (!indicatorSeriesRefs.current[type]) {
        indicatorSeriesRefs.current[type] = [];
      }

      const seriesArray = indicatorSeriesRefs.current[type];
      if (!seriesArray) {
        throw new Error(`${type} series array is not defined`);
      }

      return { chart, seriesArray };
    },
    [emitSubplotChartCreated, isLiveTrading]
  );

  return {
    initializeChartAndSeriesArray
  };
};
